/**
 * Enhanced Wetness Slider with Threshold Markers
 */

import React, { useMemo } from 'react';
import { DiaperModel } from '../../lib/types';
import { findWetnessForPercent } from '../../lib/utils';

interface WetnessSliderProps {
  value: number;
  onChange: (value: number) => void;
  diaperModel: DiaperModel | null;
  changeNeededPercent: number | null;
}

interface Preset {
  label: string;
  value: number;
  icon: string;
  color: string;
}

export const WetnessSlider: React.FC<WetnessSliderProps> = ({
  value,
  onChange,
  diaperModel,
  changeNeededPercent,
}) => {
  // Calculate threshold positions
  const thresholds = useMemo(() => {
    if (!diaperModel) return [];
    
    const markers = [];
    
    // Change needed threshold
    if (changeNeededPercent) {
      const wetness = findWetnessForPercent(changeNeededPercent, diaperModel);
      markers.push({
        value: wetness,
        label: 'Change Needed',
        color: '#ffc107',
        percent: ((wetness / 32767) * 100).toFixed(1),
      });
    }
    
    // Leak threshold
    if (diaperModel.leakThreshold?.wetnessLevel) {
      markers.push({
        value: diaperModel.leakThreshold.wetnessLevel,
        label: 'Leak',
        color: '#dc3545',
        percent: ((diaperModel.leakThreshold.wetnessLevel / 32767) * 100).toFixed(1),
      });
    }
    
    // 100% capacity
    const wetness100 = findWetnessForPercent(100, diaperModel);
    markers.push({
      value: wetness100,
      label: '100%',
      color: '#20c997',
      percent: ((wetness100 / 32767) * 100).toFixed(1),
    });
    
    return markers;
  }, [diaperModel, changeNeededPercent]);

  // Calculate preset values based on diaper model
  const presets: Preset[] = useMemo(() => {
    if (!diaperModel) {
      return [
        { label: 'Dry', value: 0, icon: '🟢', color: 'bg-green-100 hover:bg-green-200 text-green-800 border-green-300' },
        { label: 'Slightly Wet', value: 8192, icon: '💧', color: 'bg-blue-100 hover:bg-blue-200 text-blue-800 border-blue-300' },
        { label: 'Change Needed', value: 19660, icon: '⚠️', color: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-yellow-300' },
        { label: 'Leak Imminent', value: 26214, icon: '🚨', color: 'bg-red-100 hover:bg-red-200 text-red-800 border-red-300' },
        { label: 'Maximum', value: 32767, icon: '📊', color: 'bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-300' },
      ];
    }

    // Calculate wetness values for specific percentages
    const slightlyWetValue = findWetnessForPercent(8, diaperModel); // 8% volume
    const changeNeededValue = changeNeededPercent 
      ? findWetnessForPercent(changeNeededPercent, diaperModel)
      : findWetnessForPercent(15, diaperModel); // Default to 15%
    const leakValue = diaperModel.leakThreshold?.wetnessLevel || findWetnessForPercent(95, diaperModel);

    return [
      { label: 'Dry', value: 0, icon: '🟢', color: 'bg-green-100 hover:bg-green-200 text-green-800 border-green-300' },
      { label: 'Slightly Wet', value: slightlyWetValue, icon: '💧', color: 'bg-blue-100 hover:bg-blue-200 text-blue-800 border-blue-300' },
      { label: 'Change Needed', value: changeNeededValue, icon: '⚠️', color: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-yellow-300' },
      { label: 'Leak Imminent', value: leakValue, icon: '🚨', color: 'bg-red-100 hover:bg-red-200 text-red-800 border-red-300' },
      { label: 'Maximum', value: 32767, icon: '📊', color: 'bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-300' },
    ];
  }, [diaperModel, changeNeededPercent]);

  return (
    <div>
      <label className="block font-semibold mb-2">
        Wetness Level (Bytes 2-3) • 16-bit little-endian • 0-32767
      </label>
      
      {/* Quick Preset Buttons */}
      <div className="flex gap-2 flex-wrap mb-4">
        {presets.map((preset) => (
          <button
            key={preset.value}
            onClick={() => onChange(preset.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${preset.color} ${
              value === preset.value ? 'ring-2 ring-offset-1 ring-blue-500' : ''
            }`}
            title={`Set to ${preset.value}`}
          >
            {preset.icon} {preset.label}
            <span className="text-xs opacity-75 ml-1">({preset.value})</span>
          </button>
        ))}
      </div>

      {/* Slider with threshold markers */}
      <div className="relative">
        <div className="relative mb-2">
          {/* Threshold markers above slider */}
          <div className="relative h-6 mb-1">
            {thresholds.map((threshold, idx) => (
              <div
                key={idx}
                className="absolute top-0 flex flex-col items-center cursor-pointer"
                style={{ left: `${threshold.percent}%`, transform: 'translateX(-50%)' }}
                onClick={() => onChange(threshold.value)}
                title={`Click to set: ${threshold.label} (${threshold.value})`}
              >
                <div className="text-xs font-semibold whitespace-nowrap" style={{ color: threshold.color }}>
                  {threshold.label}
                </div>
                <div
                  className="w-0.5 h-2"
                  style={{ backgroundColor: threshold.color }}
                />
              </div>
            ))}
          </div>
          
          {/* Actual slider */}
          <input
            type="range"
            min="0"
            max="32767"
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      </div>

      {/* Value display */}
      <div className="flex justify-between items-center mt-3">
        <div>
          <span className="font-mono text-xl font-bold text-gray-800">{value}</span>
          <span className="text-sm text-gray-500 ml-2">
            ({((value / 32767) * 100).toFixed(1)}% of range)
          </span>
        </div>
        
        {/* Manual input */}
        <input
          type="number"
          min="0"
          max="32767"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <p className="text-xs text-gray-500 mt-2">
        💡 Click preset buttons for quick values, threshold markers to jump to specific levels, or drag the slider.
      </p>
    </div>
  );
};

